import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { shopApi } from "@/shop/shopApi";
import ProductCard from "@/shop/ProductCard";
import { ShopEmpty, ProductSkeleton } from "@/shop/ShopStates";
import { Reveal } from "@/shop/motion";

export function Collections() {
  const [data, setData] = useState({ loading: true, configured: true, collections: [] });
  useEffect(() => {
    shopApi.collections().then((d) => setData({ loading: false, configured: d.configured, collections: d.collections })).catch(() => setData({ loading: false, configured: false, collections: [] }));
  }, []);

  return (
    <div className="pt-28 pb-28" data-testid="collections-page">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4">Browse</p>
          <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.03em]">Collections</h1>
        </Reveal>
        <div className="mt-14">
          {data.loading ? (
            <div className="grid md:grid-cols-3 gap-5">{[0,1,2].map((i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-white/5 animate-pulse" />)}</div>
          ) : data.collections.length === 0 ? (
            <ShopEmpty title="Collections launching soon" note="Your Shopify collections will render here once connected." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.collections.map((c, i) => (
                <Reveal key={c.id} delay={(i % 3) * 0.08}>
                  <Link to={`/shop/collections/${c.handle}`} className="group relative block aspect-[3/4] rounded-2xl overflow-hidden bg-[#161619]" data-testid={`collection-card-${c.handle}`}>
                    {c.image && <img src={c.image} alt={c.title} loading="lazy" className="w-full h-full object-cover opacity-80 transition-all duration-[900ms] group-hover:scale-105 group-hover:opacity-100" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                    <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between">
                      <h3 className="font-display text-2xl font-semibold tracking-tight">{c.title}</h3>
                      <ArrowUpRight className="w-6 h-6 text-white/70 group-hover:text-sh-blue group-hover:rotate-45 transition-all duration-500" />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CollectionDetail() {
  const { handle } = useParams();
  const [data, setData] = useState({ loading: true, title: "", products: [] });
  useEffect(() => {
    setData({ loading: true, title: "", products: [] });
    shopApi.products({ collection: handle, first: 48 }).then((d) => setData({ loading: false, title: d.collection_title || handle, products: d.products || [] })).catch(() => setData({ loading: false, title: handle, products: [] }));
  }, [handle]);

  return (
    <div className="pt-28 pb-28" data-testid="collection-detail-page">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Link to="/shop/collections" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-8"><ArrowLeft className="w-4 h-4" /> All collections</Link>
        <Reveal><h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.03em]" data-testid="collection-title">{data.loading ? "…" : data.title}</h1></Reveal>
        <div className="mt-12">
          {data.loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">{Array.from({length: 8}).map((_, i) => <ProductSkeleton key={i} />)}</div>
          ) : data.products.length === 0 ? (
            <ShopEmpty title="No products in this collection yet" />
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="collection-product-grid">
              {data.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
